import { FormSectionHandler } from '../entity.handler';
import { AssetRatingProfileResponse, AssetRatingProfileUpdateRequest, AssetRatingPenalty } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, NumberField, EventFilterField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';
import { EventFilterService } from 'app/services/events/event-filter.service';

interface EditorValues {
    name?: string;
    items?: ListFieldValue<AssetRatingPenalty>[];
}

export class AssetRatingProfileDetailsSectionHandler implements FormSectionHandler<AssetRatingProfileResponse, EditorValues> {

    type = 'form';
    icon = ICONS.assetratingprofile;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    warning = null;

    constructor(
        public admin: AdminEditorService,
        private filters: EventFilterService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSETRATINGPROFILE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: AssetRatingProfileResponse, payload: AssetRatingProfileUpdateRequest): Promise<AssetRatingProfileResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAssetRatingProfile(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetRatingProfileResponse): Promise<EditorValues> {

        this.warning = entity.items && entity.items.length > 0 ? null : this.admin.translate(`EDITORS.ASSETRATINGPROFILE.WARNINGS.ITEM_LENGTH`);

        const result = {
            name: entity.name,
            items: (entity.items && entity.items.map(x => this.stateItemToListItem(x))) || [],
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: AssetRatingProfileUpdateRequest, values: EditorValues): Promise<AssetRatingProfileUpdateRequest> {
        let items = [];
        values.items?.forEach(item => {
            if (item.data.filter.action.eventClass === 'ai') {
                const newItem = {...item};
                newItem.data.filter.action.eventClass = 'triggeredevent';
                items.push(newItem);
            } else {
                items.push(item);
            }
        });
        

        const patch: AssetRatingProfileUpdateRequest = {
            name: values.name,
        };

        if (values.items) {
            patch.items = (items && items.map(x => x.data)) || undefined
        }
        
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return Promise.resolve(EntityUtils.mergeFields(['name', 'items'], patch, entity));
    }

    getFields(entity: AssetRatingProfileResponse): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                {
                    id: 'items', title: this.translate('FIELDS.PENALTIES'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFilterModal.bind(this), },
                        { id: 'edit', click: this.showFilterModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

    stateItemToListItem(item: AssetRatingPenalty): ListFieldValue<AssetRatingPenalty> {
        const costString = item.cost ? `, cost ${item.cost}` : '';
        const description = `${item.penalty} (${item.max} max${costString})`;

        return {
            id: item.id,
            name: item.name,
            icon: ICONS.assetratingprofile,
            description,
            data: item,
        };
    }

    updateOrAddItem(items: ListFieldValue<AssetRatingPenalty>[], existingItem: ListFieldValue<AssetRatingPenalty>, item: AssetRatingPenalty) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.stateItemToListItem(item);
        } else {
            items.push(this.stateItemToListItem(item));
        }
    }

    showFilterModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<AssetRatingPenalty> {
        return this.admin.showFormModal<AssetRatingPenalty>({
            groups: [{
                name: this.translate('MODALS.STATE.TITLE'),
                description: this.translate('MODALS.STATE.DESC'),
                fields: [
                    TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                    NumberField('penalty', this.translate('FIELDS.PENALTY'), true, 1, 100, null),
                    NumberField('max', this.translate('FIELDS.MAX_PENALTY'), true, 1, 100, null),
                    NumberField('cost', this.translate('FIELDS.COST'), true, 0, 1000000, null),
                    EventFilterField('filter', this.translate('FIELDS.FILTER'), true, this.ownerId, false, this.filters),
                ],
            }],
        }, (item && item.data) || { // pass some defaults for new items
            id: uuid.v4(),
            name: '',
            penalty: 1,
            max: 100,
            filter: null,
        }).then(result => {
            if (result) {
                this.updateOrAddItem(values.items, item, result);
            }
            return result;
        });



    }



}
